import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { Panel, ActionButton, TextField, LocalizedField } from "./_shared";
import BlocksEditor from "./BlocksEditor";
import ImageUploader from "@/components/ImageUploader";
import IconPicker from "@/components/IconPicker";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = (propertyId) => ({
  property_id: propertyId,
  slug: "",
  name: { it: "", en: "", de: "" },
  subtitle: { it: "", en: "", de: "" },
  icon: "Home",
  cover_image: null,
  password: "",
  session_days: 30,
  order: 0,
  blocks: [],
});

export default function ApartmentsManager() {
  const [propertyId, setPropertyId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async (pid) => {
    const [pRes, aRes] = await Promise.all([
      api.get("/admin/properties"),
      api.get(`/admin/apartments${pid ? `?property_id=${pid}` : ""}`),
    ]);
    setProperties(pRes.data || []);
    setItems(aRes.data || []);
    if (!pid && pRes.data?.length) setPropertyId(pRes.data.find((p) => p.is_default)?.id || pRes.data[0].id);
  };

  useEffect(() => { load(propertyId); }, [propertyId]);

  const save = async () => {
    try {
      const payload = { ...editing };
      if (!payload.password) delete payload.password;
      if (editing.id) {
        await api.patch(`/admin/apartments/${editing.id}`, payload);
      } else {
        if (!payload.password) { toast.error("Imposta una password iniziale"); return; }
        await api.post("/admin/apartments", payload);
      }
      toast.success("Salvato");
      setEditing(null);
      load(propertyId);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo appartamento?")) return;
    await api.delete(`/admin/apartments/${id}`);
    load(propertyId);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">Appartamenti</h1>
          <p className="text-xs text-ink-500 mt-1">Gestisci password, contenuti riservati e blocchi CMS</p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={propertyId || ""}
            onChange={(e) => setPropertyId(e.target.value)}
            data-testid="apartments-property-selector"
            className="text-xs bg-white border border-ink-200/60 rounded-full px-3 py-2"
          >
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <ActionButton onClick={() => setEditing(empty(propertyId))} testid="apartments-add">
            <Plus className="w-3.5 h-3.5" /> Nuovo
          </ActionButton>
        </div>
      </div>

      <Panel title="Elenco">
        <div className="divide-y divide-ink-200/30">
          {items.map((a) => (
            <div key={a.id} className="py-3 flex items-center justify-between" data-testid={`apartment-row-${a.slug}`}>
              <div>
                <div className="font-semibold">{a.name?.it || a.slug}</div>
                <div className="text-xs text-ink-500">{a.slug} · {a.session_days} giorni sessione</div>
              </div>
              <div className="flex gap-2">
                <ActionButton variant="secondary" testid={`apartment-edit-${a.slug}`} onClick={() => setEditing({ ...a, password: "" })}>Modifica</ActionButton>
                <ActionButton variant="danger" testid={`apartment-delete-${a.slug}`} onClick={() => remove(a.id)}><Trash2 className="w-3.5 h-3.5" /></ActionButton>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {editing && (
        <Panel
          title={editing.id ? "Modifica appartamento" : "Nuovo appartamento"}
          action={
            <div className="flex gap-2">
              <ActionButton variant="secondary" onClick={() => setEditing(null)}>Annulla</ActionButton>
              <ActionButton onClick={save} testid="apartment-save" disabled={uploading}>
                <Save className="w-3.5 h-3.5" /> {uploading ? "Caricamento..." : "Salva"}
              </ActionButton>
            </div>
          }
        >
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} hint="es. suite-mansarda"/>
            <IconPicker value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} testid="apartment-icon-picker" />
            <div className="md:col-span-2">
              <ImageUploader
                label="Immagine copertina"
                value={editing.cover_image}
                onChange={(v) => setEditing({ ...editing, cover_image: v || null })}
                onBusyChange={setUploading}
                testid="apartment-cover-uploader"
              />
            </div>
            <TextField label="Giorni sessione" type="number" value={editing.session_days} onChange={(v) => setEditing({ ...editing, session_days: v })} />
            <TextField label={editing.id ? "Nuova password (vuoto = invariata)" : "Password iniziale"} value={editing.password || ""} onChange={(v) => setEditing({ ...editing, password: v })} testid="apartment-password"/>
            <TextField label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
            <div className="md:col-span-2">
              <LocalizedField label="Nome" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="apartment-name"/>
            </div>
            <div className="md:col-span-2">
              <LocalizedField label="Sottotitolo" value={editing.subtitle} onChange={(v) => setEditing({ ...editing, subtitle: v })} testid="apartment-subtitle" />
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-editorial italic text-2xl mb-3">Contenuti riservati (CMS visuale)</h3>
            <BlocksEditor blocks={editing.blocks || []} onChange={(b) => setEditing({ ...editing, blocks: b })} />
          </div>
        </Panel>
      )}
    </div>
  );
}
